import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { FormEvent, MouseEvent as ReactMouseEvent } from 'react';
import styles from './SaveScheduleModal.module.css';
import ConfirmModal from '../components/ConfirmModal';
import StatusChip from '../components/StatusChip';
import { toast } from 'react-toastify';
import ApiClient from '../utils/apiClient';
import type { ApiError } from '../utils/apiClient';
import { fromGermanDayMap, toScheduleMap } from '../utils/timeSlots';
import type { DisplaySchedule } from '../utils/scheduleHelpers';

// Benutzerdefiniertes Event für das Löschen eines Stundenplans
export const SCHEDULE_DELETED_EVENT = 'scheduleDeleted';

/** Gespeicherte Schedule-Zeile (Server-Antwort bzw. Listeneintrag). */
export interface SavedSchedule {
  id: string;
  name?: string | null;
  is_primary?: boolean;
  [key: string]: unknown;
}

/** Ergebnis von saveSchedule: 'pending' = Überschreib-Dialog offen. */
export type SaveScheduleResult = SavedSchedule | 'pending' | null | undefined;

/** Imperatives Handle — Parent löst das Speichern über den Ref aus. */
export interface SaveScheduleModalHandle {
  handleSubmit: (e?: { preventDefault: () => void }) => Promise<SaveScheduleResult>;
  saveSchedule: (
    confirmedSchedule?: SavedSchedule | null,
    customName?: string | null,
    customDescription?: string | null,
  ) => Promise<SaveScheduleResult>;
}

interface SaveScheduleModalProps {
  /** Anzeige-Grid (Tagesname → Zeit → member_id). */
  schedule: DisplaySchedule;
  onSaveSuccess?: (saved: SavedSchedule) => void;
  loadedScheduleName?: string | null;
  selectedClass?: { class_id: string; name?: string | null; room?: string | null; [key: string]: unknown } | null;
  loadedScheduleId?: string | null;
  loadedScheduleIsPrimary?: boolean;
  onScheduleIdChange?: (id: string) => void;
  onPrimaryStatusChange?: (isPrimary: boolean) => void;
}

const SaveScheduleModal = forwardRef<SaveScheduleModalHandle, SaveScheduleModalProps>(({
  schedule,
  onSaveSuccess,
  loadedScheduleName,
  selectedClass,
  loadedScheduleId,
  loadedScheduleIsPrimary,
  onScheduleIdChange,
  onPrimaryStatusChange
}, ref) => {
  // Initialize state more neutrally
  const [formData, setFormData] = useState({
    scheduleName: '',
    description: ''
  });
  const [previousFormData, setPreviousFormData] = useState({
    scheduleName: '',
    description: ''
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [existingSchedule, setExistingSchedule] = useState<SavedSchedule | null>(null);
  const [schedules, setSchedules] = useState<SavedSchedule[]>([]);
  const [, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [classMembers, setClassMembers] = useState<Array<{ member_id: string; [key: string]: unknown }>>([]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleSubmit: (e?: { preventDefault: () => void }) => {
      if (e) e.preventDefault();
      return saveSchedule();
    },
    saveSchedule: (confirmedSchedule: SavedSchedule | null = null, customName: string | null = null, customDescription: string | null = null) => {
      return saveSchedule(confirmedSchedule, customName, customDescription);
    }
  }));

  // useEffect to set the name based on the prop *after* initial render and on prop changes
  useEffect(() => {
    // Function to generate a default name if none is loaded
    const generateDefaultName = () => {
      const now = new Date();
      // Ensure selectedClass exists and has a name before using it
      const classNamePrefix = selectedClass && selectedClass.name ? selectedClass.name + ' - ' : '';
      return `${classNamePrefix}${now.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    };

    if (loadedScheduleName) {
      console.log("[SaveScheduleModal useEffect] Setting name from loadedScheduleName:", loadedScheduleName);
      const newFormData = { scheduleName: loadedScheduleName, description: '' };
      setFormData(newFormData);
      setPreviousFormData(newFormData);
    } else {
      // If no loaded name, generate a default one
      console.log("[SaveScheduleModal useEffect] No loadedScheduleName found, setting default.");
      const defaultName = generateDefaultName();
      const newFormData = { scheduleName: defaultName, description: '' };
      setFormData(newFormData);
      setPreviousFormData(newFormData);
    }

    // Optionally reset description when the schedule name source changes,
    // but maybe not desired. Keep description independent for now.
    // setDescription('');

  // Depend on the prop that carries the name and selectedClass (for default name generation)
  }, [loadedScheduleName, selectedClass]);

  // Lade Klassenmitglieder und existierende Stundenpläne beim Mounten
  useEffect(() => {
    if (!selectedClass || !selectedClass.class_id) return;
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Lade Klassenmitglieder
        const membersResponse = await ApiClient.get(`classes/${selectedClass.class_id}/members`);
        // ApiClient wirft bei !ok - kein manueller Check nötig
        const membersData = await membersResponse.json();
        setClassMembers(membersData.members || []);

        // Lade existierende Stundenpläne
        const schedulesResponse = await ApiClient.get(`classes/${selectedClass.class_id}/schedules`);
        // ApiClient wirft bei !ok - kein manueller Check nötig
        const schedulesData = await schedulesResponse.json();
        setSchedules(schedulesData.schedules || []);
      } catch (error) {
        console.error('Error:', error);
        setError('Fehler beim Laden der Daten');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedClass]);

  const handleInputClick = (e: ReactMouseEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).select(); // Markiert den gesamten Text beim Klicken
  };

  const saveSchedule = async (
    confirmedSchedule: SavedSchedule | null = null,
    customName: string | null = null,
    customDescription: string | null = null,
  ): Promise<SaveScheduleResult> => {
    if (!selectedClass || !selectedClass.class_id) {
      toast.error('Kein Kurs ausgewählt');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // --- BEGIN CONVERSION ---
      // Konvertiert das Frontend-Schedule-Format über das geteilte timeSlots-
      // Modul statt einer eigenen, unshared Implementierung (Gegenstück:
      // App.js::handleScheduleLoad). Member-ID-Validierung bleibt lokal, da sie
      // speicher-spezifische Business-Logik ist, keine reine Formatkonversion.
      // toScheduleMap sortiert bereits chronologisch und garantiert alle 7
      // Tages-Schlüssel — beides musste zuvor separat sichergestellt werden.

      // Erstelle Set mit gültigen Member-IDs für schnelle Validierung
      const validMemberIds = new Set(classMembers.map(member => member.member_id));
      console.log('Valid member IDs:', validMemberIds);

      // Tagesnamen defensiv auf Großschreibung normalisieren (wie zuvor).
      const normalizedSchedule: DisplaySchedule = {};
      Object.entries(schedule).forEach(([dayName, timeSlots]) => {
        normalizedSchedule[dayName.toUpperCase()] = timeSlots;
      });

      const assignment = fromGermanDayMap(normalizedSchedule);
      Array.from(assignment.entries()).forEach(([slot, student]) => {
        const isValidString = typeof student === 'string' && student.trim().length > 0;
        if (!isValidString) {
          assignment.delete(slot);
          return;
        }
        if (!validMemberIds.has(student)) {
          console.warn(`Ungültige Member-ID: ${student} - nicht in aktuellem Kurs gefunden`);
          assignment.delete(slot);
        }
      });

      const convertedTimes = toScheduleMap(assignment);
      // --- END CONVERSION ---

      const scheduleData = {
        name: customName || formData.scheduleName,
        description: customDescription || formData.description,
        available_times: convertedTimes,
        class_id: selectedClass.class_id,
      };

      let response;
      
      // Wenn ein confirmedSchedule übergeben wurde oder existingSchedule gesetzt ist.
      // `customName` heißt: Aufrufer will ausdrücklich einen NEUEN Plan (z. B.
      // „neuer Entwurf" statt Überschreiben eines freigegebenen Plans). Ohne diese
      // Bedingung überschrieb ein zuvor abgebrochener Überschreiben-Dialog den
      // freigegebenen Plan, weil `existingSchedule` gesetzt blieb (Audit H7).
      const scheduleToUpdate = confirmedSchedule || (customName ? null : existingSchedule);
      
      if (scheduleToUpdate) {
        console.log('Prüfe, ob Stundenplan noch existiert:', scheduleToUpdate.id);

        // Prüfe, ob der Stundenplan noch existiert
        try {
          await ApiClient.get(`schedules/${scheduleToUpdate.id}`);
          console.log('Aktualisiere existierenden Stundenplan:', scheduleToUpdate.id);
          response = await ApiClient.post(`classes/${selectedClass.class_id}/schedules/${scheduleToUpdate.id}`, scheduleData);
        } catch (caught) {
          const checkError = caught as ApiError;
          if (checkError.status === 404) {
            console.log('Stundenplan existiert nicht mehr, erstelle einen neuen');
            // Wenn der Stundenplan nicht mehr existiert, erstelle einen neuen
            response = await ApiClient.post(`classes/${selectedClass.class_id}/schedules`, scheduleData);
          } else {
            throw checkError;
          }
        }
      } else {
        // Prüfe, ob ein Stundenplan mit diesem Namen bereits existiert
        const existingScheduleWithName = schedules.find(s => s.name === (customName || formData.scheduleName));
        
        if (existingScheduleWithName && !customName) {
          // Prüfe, ob dieser Stundenplan noch existiert
          try {
            await ApiClient.get(`schedules/${existingScheduleWithName.id}`);
            // Wenn ja, zeige Bestätigungsdialog und beende die Funktion
            console.log('Stundenplan mit gleichem Namen gefunden:', existingScheduleWithName.id);
            setExistingSchedule(existingScheduleWithName);
            setShowConfirmModal(true);
            setIsLoading(false);
            return 'pending';
          } catch (caught) {
            const checkError = caught as ApiError;
            if (checkError.status === 404) {
              console.log('Stundenplan mit gleichem Namen existiert nicht mehr');
              // Stundenplan existiert nicht mehr, erstelle einen neuen
            } else {
              console.log('Fehler beim Prüfen des existierenden Stundenplans:', checkError);
              // Bei anderem Fehler, versuche einen neuen zu erstellen
            }
          }
        }
        
        // Erstelle neuen Stundenplan
        console.log('Erstelle neuen Stundenplan');
        response = await ApiClient.post(`classes/${selectedClass.class_id}/schedules`, scheduleData);
      }

      // ApiClient wirft bei !ok - kein manueller Check nötig
      const savedSchedule: SavedSchedule = await response.json();

      // Rufe die korrekte Prop-Funktion auf
      if (typeof onSaveSuccess === 'function') {
        onSaveSuccess(savedSchedule); // Verwende onSaveSuccess
      } else {
        console.warn("onSaveSuccess prop is not a function or was not provided.");
      }

      // Update parent state with saved schedule ID and is_primary
      if (savedSchedule.id && onScheduleIdChange) {
        onScheduleIdChange(savedSchedule.id);
      }
      if (onPrimaryStatusChange && savedSchedule.is_primary !== undefined) {
        onPrimaryStatusChange(savedSchedule.is_primary);
      }

      // Aktualisiere die Liste der Stundenpläne
      try {
        const updatedSchedulesResponse = await ApiClient.get(`classes/${selectedClass.class_id}/schedules`);
        const data = await updatedSchedulesResponse.json();
        setSchedules(data.schedules || []);
      } catch (refreshError) {
        console.warn('Konnte Stundenpläne nicht aktualisieren:', refreshError);
      }
      
      // Zurücksetzen des Zustands nach erfolgreichem Speichern
      setExistingSchedule(null);
      setShowConfirmModal(false);
      
      return savedSchedule;
    } catch (caught) {
      const error = caught as ApiError;
      console.error('Fehler beim Speichern des Stundenplans:', error);
      // Verwende originalErrorData.detail für Backend-Fehlermeldungen
      const errorMsg = error.originalErrorData?.detail?.[0]?.msg ||
                       (typeof error.originalErrorData?.detail === 'string' ? error.originalErrorData.detail : null) ||
                       JSON.stringify(error.originalErrorData?.detail) ||
                       error.message;
      setError(`Fehler: ${errorMsg}`);
      toast.error(`Fehler: ${errorMsg}`);
      return null; // Fehler
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.scheduleName.trim()) {
      toast.error('Bitte geben Sie einen Namen für den Stundenplan ein');
      return;
    }
    
    // Prüfe, ob sich die Formulardaten geändert haben
    const hasNameChanged = formData.scheduleName.trim() !== previousFormData.scheduleName.trim();
    const hasDescriptionChanged = formData.description.trim() !== previousFormData.description.trim();
    
    if (!hasNameChanged && !hasDescriptionChanged) {
      console.log('Formulardaten haben sich nicht geändert, kein Speichern erforderlich');
      return;
    }
    
    // Aktualisiere die vorherigen Formulardaten nach dem Speichern
    setPreviousFormData({
      scheduleName: formData.scheduleName.trim(),
      description: formData.description.trim()
    });
    
    await saveSchedule();
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <div className={styles.statusChipWrap} title={loadedScheduleIsPrimary
              ? 'Studierende sehen ihren Termin aus diesem Plan'
              : loadedScheduleId ? 'Gespeichert, aber für Studierende nicht sichtbar' : 'Noch nicht gespeichert'}>
              {loadedScheduleIsPrimary ? (
                <StatusChip tone="ok">Freigegeben</StatusChip>
              ) : loadedScheduleId ? (
                <StatusChip tone="neutral">Entwurf</StatusChip>
              ) : (
                <StatusChip tone="warn">Nicht gespeichert</StatusChip>
              )}
            </div>
            <form onSubmit={handleSubmit}>
              <div className={styles.nameInputRow}>
                
                <input
                  id="scheduleNameInput"
                  type="text"
                  value={formData.scheduleName}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduleName: e.target.value }))}
                  onClick={handleInputClick}
                  placeholder="Name des Stundenplans"
                  required
                />
              </div>
              <textarea
                id="scheduleDescriptionInput"
                placeholder="Beschreibung (optional)"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
              {selectedClass?.room && (
            <div className={styles.classRoomInfo} title="(Raum editierbar über 'Deine Kurse' / 'Kurs bearbeiten')">
              <span className={styles.roomLabel} >Raum:</span> {selectedClass.room}
            </div>
          )}
            </form>
            {/* <div className={styles.buttons}>
              <button 
                onClick={handleSubmit} 
                disabled={isLoading}
                className={isLoading ? styles.loading : ''}
              >
                {isLoading ? 'Speichern...' : 'Speichern'}
              </button>
            </div> */}
            {error && <div className={styles.error}>{error}</div>}
            
          </div>
          
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setExistingSchedule(null); }}
          onConfirm={() => {
            // Übergebe den existingSchedule direkt an saveSchedule
            if (existingSchedule) {
              saveSchedule(existingSchedule);
            }
          }}
          title="Stundenplan überschreiben?"
          message={`Ein Stundenplan mit dem Namen "${formData.scheduleName}" existiert bereits. Möchten Sie diesen überschreiben?`}
        />
      )}
    </> 
  );
});

export default SaveScheduleModal; 